import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAnswerRequest } from '@/api/questionnaires/responses';
import type { AnswerRequest } from '../-types';
import { normalizeAnswerRequest } from '../-utils/answerSerializers';

export const answerRequestQueryKey = (id: string) =>
  ['questionnaires', 'answer-request', id] as const;

/** TanStack `queryOptions` so the route loader and the page hook share
 *  the same key / queryFn — `loader: queryClient.ensureQueryData(...)`
 *  warms the cache before the page mounts. The queryFn normalises the
 *  raw wire shape (legacy field names + responder-specific per-question
 *  fields) into a clean `AnswerRequest`. */
export const answerRequestQueryOptions = (id: string) =>
  queryOptions<AnswerRequest>({
    queryKey: answerRequestQueryKey(id),
    queryFn: async () => {
      // The migrated read answers `{metadata, data}` — unwrap here, since the
      // data layer returns the raw envelope.
      const { data } = await fetchAnswerRequest(id);
      return normalizeAnswerRequest(data, id);
    },
    enabled: id.length > 0,
  });

/** Loads the questionnaire request + the responder's saved draft.
 *  Single source of truth for the responder page — every per-question
 *  save invalidates this query so the page refetches the canonical
 *  server state. */
export function useAnswerRequestQuery(id: string) {
  return useQuery(answerRequestQueryOptions(id));
}
